import type { StrategyNode } from '../types/BaseNode';

export class UndoHandler {
    private undoStack: StrategyNode[] = [];
    private redoStack: StrategyNode[] = [];
    private maxSize: number;

    constructor(maxSize: number = 20) {
        this.maxSize = maxSize;
    }

    push(current: StrategyNode) {
        const last = this.undoStack[this.undoStack.length - 1];

        // Only push if tree structure has changed
        if (last) {
            const hashLast = this.hashTreeStructure(last)
            const hashCurrent = this.hashTreeStructure(current)
            if (hashLast == hashCurrent) {
                // Replace the previous version with the latest (preserving ID and config changes
                this.undoStack[this.undoStack.length - 1] = structuredClone(current);
                this.redoStack = [];
                return;
            }
        }

        this.undoStack.push(structuredClone(current));
        if (this.undoStack.length > this.maxSize) {
            this.undoStack.shift();
        }
        this.redoStack = [];
    }

    hashTreeStructure(node: StrategyNode): string {
        switch (node.type) {
            case 'Single':
                // Include typeId and props for property-level undo
                const sType = (node as any).config?.typeId ?? '';
                const sProps = JSON.stringify((node as any).config?.props ?? {});
                return `S(${sType}|${sProps})`;

            case 'Composite':
                // Include operator
                const op = (node as any).config?.operator ?? '';
                const leftHash = (node as any).config?.left
                    ? this.hashTreeStructure((node as any).config.left)
                    : 'null';
                const rightHash = (node as any).config?.right
                    ? this.hashTreeStructure((node as any).config.right)
                    : 'null';
                return `C:${op}(${leftHash}|${rightHash})`;

            case 'Formatter':
                // Include typeId and props
                const fType = (node as any).config?.typeId ?? '';
                const fProps = JSON.stringify((node as any).config?.props ?? {});
                const childHash = (node as any).config?.child
                    ? this.hashTreeStructure((node as any).config.child)
                    : 'null';
                return `F:${fType}(${fProps}|${childHash})`;

            case 'PnL':
                const maxPos = (node as any).config?.maxLots ?? 1;
                const lSize = (node as any).config?.lotSize ?? 1;
                const fs = (node as any).config?.fees ?? 0;
                const mode = (node as any).config?.mode ?? 'long';
                const entryHash = (node as any).config?.entry
                    ? this.hashTreeStructure((node as any).config.entry)
                    : 'null';
                const exitHash = (node as any).config?.exit
                    ? this.hashTreeStructure((node as any).config.exit)
                    : 'null';
                return `P:${maxPos}:${lSize}:${fs}:${mode}(${entryHash}|${exitHash})`;

            default:
                return '';
        }
    }


    undo(current: StrategyNode): StrategyNode | null {
        if (this.undoStack.length === 0) {
            return null;
        }

        const hashCurrent = this.hashTreeStructure(current);

        while (this.undoStack.length > 0) {
            const previous = this.undoStack.pop();
            if (!previous) {
                return null;
            }

            const hashLast = this.hashTreeStructure(previous);

            if (hashLast !== hashCurrent) {
                this.redoStack.push(structuredClone(current)); // ✅ store current
                return previous;
            }
        }

        return null; // ⛔ nothing different to undo to
    }


    redo(current: StrategyNode): StrategyNode | null {
        const next = this.redoStack.pop();
        if (!next) return null;

        this.undoStack.push(structuredClone(current)); // ✅ store current into undo
        return next;
    }


}
